import React from 'react';
import { ChevronDown, ChevronRight, MoreVertical } from 'lucide-react';
import { Button } from "@/modern/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { useTimelineContext } from '../context/TimelineContext';
import { FilterKey, TimelineMode } from '../utils/types';
import { VIEW_TYPES } from '@/other/constants/OtherKeys';
import { SWIVU_STANDARD_ICONS } from '@/other/constants/IconKeys';

// Mode configuration with labels and icons
export const MODE_CONFIG: Record<
  FilterKey,
  {
    label: string;
    icon: string;
  }
> = {
  status: {
    label: "Status Changes",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks] // "fa-light fa-swap-arrows"
  },
  schedule: {
    label: "Scheduled Resources",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.schedulerView] // "fa-light fa-calendar-days"
  },
  planning: {
    label: "Planning Events",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.planView] // "fa-light fa-diagram-predecessor"
  },
  reminders: {
    label: "Reminders",
    icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.reminder] // "fa-light fa-alarm-clock"
  },
  history: {
    label: "History",
    icon: "fa-light fa-clock-rotate-left" // Font Awesome Pro - not in standard icons
  }
};

interface TimelineHeaderProps {
  isTimelineOpen: boolean;
  setIsTimelineOpen: (isOpen: boolean) => void;
  hasEvents: boolean;
}

export const TimelineHeader: React.FC<TimelineHeaderProps> = ({ isTimelineOpen, setIsTimelineOpen }) => {
  const {
    config,
    selectedMode,
    setSelectedMode,
    handleCreateReminder,
    handleCreateSchedule,
    handleCreatePlanning
  } = useTimelineContext();

  // Get context menu items based on selected mode
  const getContextMenuItems = () => {
    const items: React.ReactNode[] = [];

    if (selectedMode === 'reminders' && config.createActions.includes('reminder')) {
      items.push(
        <DropdownMenuItem key="add-reminder" onClick={handleCreateReminder}>
          <i className={`${MODE_CONFIG.reminders.icon} h-4 w-4 mr-2`} />
                Add Reminder
              </DropdownMenuItem>
      );
    }

    if (selectedMode === 'schedule' && config.createActions.includes('schedule')) {
      items.push(
        <DropdownMenuItem key="add-schedule" onClick={handleCreateSchedule}>
          <i className={`${MODE_CONFIG.schedule.icon} h-4 w-4 mr-2`} />
                Add Schedule Date
              </DropdownMenuItem>
      );
    }

    if (selectedMode === 'planning' && config.createActions.includes('planning')) {
      items.push(
        <DropdownMenuItem key="add-planning" onClick={handleCreatePlanning}>
          <i className={`${MODE_CONFIG.planning.icon} h-4 w-4 mr-2`} />
                Add Planning Date
              </DropdownMenuItem>
      );
    }

    return items;
  };

  const contextMenuItems = getContextMenuItems();
  const hasContextMenu = contextMenuItems.length > 0;

  // Collapsed view - vertical tab on the left
  if (!isTimelineOpen) {
    return (
      <div className="flex-none border-b">
        <button
          onClick={() => setIsTimelineOpen(true)}
          className="w-full h-full py-2 sm:py-3 px-2 hover:bg-muted/50 transition-colors group flex items-center justify-center"
        >
          <div className="flex flex-col items-center gap-2">
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            <div className="text-sm tracking-tight text-foreground [writing-mode:vertical-rl] rotate-180">
              Timeline
            </div>
          </div>
        </button>
      </div>
    );
  }

  // Expanded view - normal horizontal layout with collapsible button
  return (
    <div className="flex-none border-b">
      <div className="py-2 sm:py-3 px-3 sm:px-6">
        <div className="flex flex-col 2xl:flex-row items-stretch 2xl:items-center gap-3 flex-wrap">
          {/* Title - Always collapsible */}
          <button
            onClick={() => setIsTimelineOpen(false)}
            className="flex items-center gap-2 group -ml-2 px-2 py-1 rounded-md hover:bg-muted/50 transition-colors shrink-0"
          >
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-foreground" />
            <div className="text-base tracking-tight text-foreground">Timeline</div>
          </button>

          {/* Right side: Select and Menu */}
          <div className="flex items-center justify-end gap-2 flex-1 min-w-[13.33rem]">
            {/* Mode Selector - Responsive */}
            <Select
              value={selectedMode || ""}
              onValueChange={(value) => setSelectedMode(value as TimelineMode)}
            >
              <SelectTrigger className="h-8 w-auto gap-2">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {config.availableFilters.map((filter) => {
                  const modeConfig = MODE_CONFIG[filter];
                  return (
                    <SelectItem key={filter} value={filter} textValue={modeConfig.label}>
                      <div className="flex items-center gap-2">
                        <i className={`${modeConfig.icon} text-xs shrink-0`} />
                        <span className="text-sm truncate">{modeConfig.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Context Menu */}
            {hasContextMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0">
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">Timeline actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {contextMenuItems}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
